import * as THREE from "three";

export class BloodParticleSystem {
  constructor(scene, blockManager, obstacleManager) {
    this.scene = scene;
    this.blockManager = blockManager;
    this.obstacleManager = obstacleManager;
    this.particles = [];
    this.active = false;

    // Conversion from pixels to world units (100px = 1 unit)
    this.pxToUnit = 0.01;

    // Physics constants
    this.baseGravityPerFrame = 0.005; // Base gravity amount per frame at 60fps
    this.baseVelocityPerFrame = 0.05; // Base velocity per frame at 60fps
    this.targetFrameTime = 16.67; // Target frame time (60fps)

    // Camera view boundaries (will be updated from Game class)
    this.cameraViewBoundaries = {
      left: -100,
      right: 100,
      top: 100,
      bottom: -100,
    };

    // Shared geometry + materials for all blood particles. Per-particle size is
    // applied via mesh.scale; settling swaps the material reference to the
    // (dimmer) settled material rather than mutating a per-particle one. These
    // are reused across deaths and freed once in dispose().
    this.sharedGeometry = new THREE.PlaneGeometry(1, 1);
    this.flyingMaterial = new THREE.MeshStandardMaterial({
      color: 0xff0000,
      roughness: 0.1,
      metalness: 0.1,
      emissive: 0xff4444,
      emissiveIntensity: 1.0,
    });
    this.settledMaterial = new THREE.MeshStandardMaterial({
      color: 0xff0000,
      roughness: 0.1,
      metalness: 0.1,
      emissive: 0xff2222,
      emissiveIntensity: 0.8,
    });
  }

  // Method to update camera view boundaries
  updateCameraViewBoundaries(camera, cameraOffsetX) {
    // Calculate camera view boundaries based on camera position and frustum
    this.cameraViewBoundaries = {
      left: camera.position.x - camera.right - 1, // Extra margin
      right: camera.position.x + camera.right + 1,
      top: camera.position.y + camera.top + 1,
      bottom: camera.position.y + camera.bottom - 1,
    };
  }

  createParticles(position, count = 100) {
    // Clear any existing particles
    this.clearParticles();

    // Mark system as active
    this.active = true;

    for (let i = 0; i < count; i++) {
      // Random size between 2px and 10px (0.02 to 0.1 units)
      const size = (Math.random() * 8 + 2) * this.pxToUnit;

      // Shared geometry/material; size applied via scale (unit-square geometry).
      const particle = new THREE.Mesh(this.sharedGeometry, this.flyingMaterial);
      particle.scale.set(size, size, 1);

      // Position particle at player's position
      particle.position.set(position.x, position.y, 0.1);

      // Store initial position for collision detection
      particle.userData.initialPosition = {
        x: particle.position.x,
        y: particle.position.y,
        z: particle.position.z,
      };

      // Define velocities in terms of units per second, not per frame
      // This makes velocities frame rate independent
      particle.userData.velocity = {
        x: (Math.random() * 0.12 - 0.06) * this.baseVelocityPerFrame * 60 * 20, // Triple the horizontal velocity
        y: (Math.random() * 0.15 + 0.02) * this.baseVelocityPerFrame * 60 * 20, // Triple the vertical velocity
        z: 0,
      };

      // Random gravity effect per particle for varied falling speeds - store as base gravity multiplier
      particle.userData.gravityFactor = 20; // Results in 0.8x to 2.0x gravity variation

      // Track if particle has settled
      particle.userData.settled = false;

      // Random chance for particles to penetrate obstacles
      // Higher values = more likely to stop on collision
      particle.userData.penetrationResistance = Math.random();

      // Track distance traveled for penetration calculations
      particle.userData.distanceTraveled = 0;

      // Size of particle bounds for collision detection
      particle.userData.collisionSize = size * 0.5; // Half the visual size

      // Enable shadows
      particle.castShadow = true;
      particle.receiveShadow = false;

      // Add to scene and store in array
      this.scene.add(particle);
      this.particles.push(particle);
    }
  }

  update(deltaGameSpeed) {
    if (!this.active) return;

    let allSettled = true;

    // Calculate time factor in seconds (deltaTime in seconds)
    // This is more reliable than the previous approach
    const deltaTimeSeconds = (deltaGameSpeed * this.targetFrameTime) / 1000;

    this.particles.forEach((particle) => {
      if (particle.userData.settled) return;

      allSettled = false;

      // Store previous position for distance calculation
      const prevPosition = {
        x: particle.position.x,
        y: particle.position.y,
        z: particle.position.z,
      };

      // Apply gravity to vertical velocity - using units/second
      const gravityThisFrame =
        this.baseGravityPerFrame *
        60 *
        particle.userData.gravityFactor *
        deltaTimeSeconds *
        3; // Triple the gravity effect
      particle.userData.velocity.y -= gravityThisFrame;

      // Apply velocity to position - using units/second
      particle.position.x += particle.userData.velocity.x * deltaTimeSeconds;
      particle.position.y += particle.userData.velocity.y * deltaTimeSeconds;
      // Z position remains constant at 0.1

      // Calculate distance moved this frame
      const dx = particle.position.x - prevPosition.x;
      const dy = particle.position.y - prevPosition.y;
      const frameDistance = Math.sqrt(dx * dx + dy * dy);

      // Update total distance traveled
      particle.userData.distanceTraveled += frameDistance;

      // Check ONLY if particle has gone below the bottom of the camera view
      if (particle.position.y < this.cameraViewBoundaries.bottom) {
        this.settleParticle(particle);
        return;
      }

      // Calculate chance to stick based on distance traveled
      // Particles are less likely to penetrate the longer they've traveled
      const stickChance = Math.min(
        particle.userData.distanceTraveled / 1.5,
        0.5
      );
      const willStick = Math.random() < stickChance;

      // Skip collision checks if the particle won't stick this frame
      if (!willStick) {
        return;
      }

      // Check collision with blocks using center point
      if (this.blockManager) {
        // Pass particle center position for collision check
        const collidingBlock = this.blockManager.checkBlockCollision({
          x: particle.position.x,
          y: particle.position.y,
          // Include size if needed by the check method, though center check is simpler
          // size: particle.userData.collisionSize
        });

        // If checkBlockCollision returns a block object, it means collision occurred
        if (collidingBlock) {
          // Stop the particle at its current position
          particle.position.z = 0.1; // Ensure it's slightly in front
          this.settleParticle(particle);
          return; // Stop further checks for this particle
        }
      }

      // Check collision with obstacles using center point
      if (this.obstacleManager) {
        const obstacles = this.obstacleManager.getObstacles();

        for (const obstacle of obstacles) {
          const obstacleBounds = obstacle.userData.bounds;
          const isSpike = obstacle.userData.isSpike;

          // Check if center point is within obstacle bounds
          const centerInBounds =
            particle.position.x >= obstacleBounds.left &&
            particle.position.x <= obstacleBounds.right &&
            particle.position.y >= obstacleBounds.bottom &&
            particle.position.y <= obstacleBounds.top;

          if (centerInBounds) {
            // For spikes, do a more precise triangle check
            if (isSpike) {
              // Get spike triangle dimensions
              const triangleBase = obstacleBounds.right - obstacleBounds.left;
              const triangleHeight = obstacleBounds.top - obstacleBounds.bottom;
              const spikeBaseY = obstacleBounds.bottom;

              // Calculate relative horizontal position
              const particleX = particle.position.x;
              const relativePosX =
                (particleX - obstacleBounds.left) / triangleBase;

              // Calculate triangle height at this x-position
              const heightAtX =
                triangleHeight * (1 - 2 * Math.abs(relativePosX - 0.5));
              const triangleEdgeY = spikeBaseY + heightAtX;

              // *** IMPORTANT: Check if particle is actually below the triangle edge ***
              if (particle.position.y <= triangleEdgeY) {
                // Particle collided with the actual spike triangle
                particle.position.z = 0.1; // Ensure it's slightly in front
                this.settleParticle(particle);
                return; // Collision handled for this particle
              }
              // If particle.position.y > triangleEdgeY, it's above the spike, no collision.
            } else {
              // Regular (non-spike) obstacle collision logic
              const vx = particle.userData.velocity.x;
              const vy = particle.userData.velocity.y;

              // Reposition based on velocity (existing logic)
              if (Math.abs(vx) > Math.abs(vy)) {
                particle.position.x =
                  vx > 0
                    ? obstacleBounds.left - particle.userData.collisionSize
                    : obstacleBounds.right + particle.userData.collisionSize;
              } else {
                particle.position.y =
                  vy > 0
                    ? obstacleBounds.bottom - particle.userData.collisionSize
                    : obstacleBounds.top + particle.userData.collisionSize;
              }
              // Settle the particle after collision with non-spike obstacle
              particle.position.z = 0.1;
              this.settleParticle(particle);
              return; // Collision handled for this particle
            }
          }
        }
      }
    });

    // If all particles have settled, mark system as inactive
    if (allSettled) {
      this.active = false;
    }
  }

  // Helper method to settle a particle in one place
  settleParticle(particle) {
    particle.userData.settled = true;

    // Stop movement
    particle.userData.velocity.x = 0;
    particle.userData.velocity.y = 0;
    particle.userData.velocity.z = 0;

    // Swap to the shared (dimmer) settled material for the settled look.
    particle.material = this.settledMaterial;
  }

  clearParticles() {
    // Remove all existing particles. Geometry/materials are shared, so they are
    // NOT disposed here — only once in dispose() at teardown.
    this.particles.forEach((particle) => {
      this.scene.remove(particle);
    });

    this.particles = [];
    this.active = false;
  }

  // Reset all blood particles
  reset() {
    this.clearParticles();
  }

  // Dispose shared GPU resources. Call once when the system is torn down.
  dispose() {
    this.clearParticles();
    this.sharedGeometry.dispose();
    this.flyingMaterial.dispose();
    this.settledMaterial.dispose();
  }
}

export default BloodParticleSystem;
